import { $log } from '../../y/debug';
import { onAttach, onDetach } from '../attach';
import { $el } from '../elements';
import { UIKey } from '../key';
import { uiHidePopup, uiShowPopup, UIPopupOptions } from '../popup';
import { $svg, UISVG } from '../svg';
import { UIRelPos, UIValue } from '../types';
import { UIView, UIViewSpec } from '../view';
import { UIButton } from './button';
import { UITextInput } from './input';

export function checkFilterGenerator(v: UIValue<any>) {
    const box = new UISVG('r-box', [24, 24]);

    box.add($svg.RECT(3, 3, 18, 18, 'r-fill-rect-frame'));
    box.add($svg.RECT(3, 3, 18, 18, 'r-fill-rect'));
    box.add($svg.POLYLINE('7.1,11.3 10.7,14.7 17.6,7.7', 'r-fill-tick'));

    return { view: $el.DIV('ui-filter-item ui-toggle', [box, v.name]), search: v.name };
}

export function singleFilterGenerator(v: UIValue<any>) {
    return { view: $el.DIV('ui-filter-item', v.name), search: v.name };
}

interface UIFilterPopupOptions {
    no_search?: boolean;
    single?: boolean;
}

interface Entry<Model> {
    view: UIViewSpec;
    model: Model;
    checked?: boolean;
    search?: string;
    hidden?: boolean;
}

export class UIFilterPopup<Key> extends UIView {
    private _options: UIFilterPopupOptions;
    private _selectable: Entry<UIValue<Key>>[];
    private _selected: number;

    on_filter?: (values: UIValue<Key>[]) => void;

    constructor(models: UIValue<Key>[], selected?: Key[], options: UIFilterPopupOptions = {}) {
        super('div', 'ui-filter-popup');

        const selected_set = new Set(selected);

        const entries: Entry<UIValue<Key>>[] = models.map(m => {
            const item = options.single ? singleFilterGenerator(m) : checkFilterGenerator(m);
            const checked = selected_set.has(m.key);
            if(checked)
                item.view.classList.add('f-checked');
            return item && { view: item.view, model: m, selectable: true, checked, search: item.search };
        }).filter(e => !!e);

        entries.sort((a, b) => {
            if(a.checked) {
                if(!b.checked) return -1;
            } else if(b.checked) {
                return 1;
            }
            return a.model.name.localeCompare(b.model.name)
        });

        this._options = options;
        this._selected = -1;

        const selectable = this._selectable = [...entries];

        const list = $el.DIV('r-list');

        for(const entry of entries) {
            const view = entry.view;
            list.ui_add(view);
            view.ui_elem.addEventListener('click', ev => {
                ev.preventDefault();
                this._doChoose(entry);
            });
            view.ui_elem.addEventListener('mousedown', ev => {
                ev.preventDefault();
            });
        }

        let input: UITextInput | undefined;

        if(!options.no_search)
            input = new UITextInput({ kind: 'k-flat' });

        this.ui_add([
            input,
            list,
            !options.single && new UIButton({
                kind: 'k-text', title: 'FILTER', on_click: () => {
                    this.on_filter?.(entries.filter(e => e.checked).map(e => e.model));
                }
            })
        ]);

        let key_down: ((ev: KeyboardEvent) => void) | undefined;
        let search_input_handler: ((ev: Event) => void) | undefined;

        // TODO: move the key capture functionality to showPopup
        onAttach(this, () => {
            document.addEventListener('keydown', key_down = ev => {
                if(ev.key === UIKey.Up) {
                    ev.preventDefault();
                    this.selectPrevious();
                } else if(ev.key === UIKey.Down) {
                    ev.preventDefault();
                    this.selectNext();
                } else if(ev.key === UIKey.Enter) {
                    if(this._selected >= 0) {
                        ev.preventDefault();
                        this._doChoose(selectable[this._selected]);
                    }
                }
            });

            if(input) {
                input.ui_elem.addEventListener('input', search_input_handler = () => {
                    const query = input.value;
                    $log('app', 'dropdown search', query);
                    let first: number | undefined;
                    for(let i = 0; i < selectable.length; i++) {
                        const e = selectable[i];
                        const show = !e.search || e.search.toLowerCase().includes(query)
                        e.view?.ui_show(undefined, show);
                        e.hidden = !show;
                        if(show && !first)
                            first = i;
                    }
                });

                input.focus();
            }
        });

        onDetach(this, () => {
            key_down && document.removeEventListener('keydown', key_down);
            if(input) {
                search_input_handler && input.ui_elem.removeEventListener('input', search_input_handler);
            }
        });
    }

    get has_selection() {
        return this._selected >= 0;
    }

    selectPrevious() {
        if(!this._selectable.length) return;

        for(let i = this._selected - 1; i >= 0; i--) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }

        for(let i = this._selectable.length - 1; i >= this._selected + 1; i--) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }
    }

    selectNext() {
        if(!this._selectable.length) return;

        for(let i = this._selected + 1; i < this._selectable.length; i++) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }

        for(let i = 0; i < this._selected; i++) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }
    }

    private _doChoose(entry: Entry<UIValue<Key>>) {
        if(this._options.single) {
            this.on_filter?.([entry.model]);
        } else {
            entry.checked = !entry.checked;
            entry.view.classList.toggle('f-checked', entry.checked);
        }
    }

    private _doSelect(n: number) {
        if(n == this._selected) return;

        if(this._selected >= 0) {
            const view = this._selectable[this._selected].view;
            view && view.classList.remove('f-selected')
        }

        this._selected = n;

        if(n < 0) return;

        const view = this._selectable[this._selected].view;
        if(!view) return;
        view.classList.add('f-selected');

        const elem = view.ui_elem;

        const top = elem.offsetTop;
        const bottom = top + elem.offsetHeight;

        if(top < this.root.scrollTop) {
            this.root.scrollTop = top;
        } else if(bottom > this.root.scrollTop + this.root.offsetHeight) {
            this.root.scrollTop = bottom - this.root.offsetHeight;
        }
    }

    open(ref: UIViewSpec, options: UIPopupOptions = { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, match_size: true, adjust_size: true, can_flip: true, prevent_blur: true }) {
        uiShowPopup(this, ref, options);
    }

    close(close_parent?: boolean) {
        uiHidePopup(this, close_parent);
    }
}
